if (!customElements.get('sheeting-picker')) {
  class SheetingPicker extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {
        debug: false,
      };

      // Get translation
      this.translations = parseJson(this.dataset.translations);

      // Selectors
      this.form = this.querySelector('form');
      this.inputs = this.querySelectorAll('input');
      this.submitButton = this.querySelector('[type="submit"]');
      this.closeButton = this.querySelector('[data-close-drawer]');

      // Configurator selectors
      this.sheetingButton = document.querySelector('[data-sheeting-button]');
      this.sheetingPlaceholder = document.querySelector('[data-sheeting-placeholder]');
      this.sheetingImage = this?.sheetingPlaceholder.querySelector(
        '[data-sheeting-placeholder-image]'
      );
      this.sheetingTitle = this?.sheetingPlaceholder.querySelector(
        '[data-sheeting-placeholder-title]'
      );

      // Event handlers
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.querySelectorAll('input').forEach((input) => {
        input.addEventListener('change', this.onChange.bind(this));
      });
    }

    /**
     * On change input
     * @param {object} event
     */
    onChange(event) {
      this.inputs.forEach((input) => {
        input.closest('[data-option]').classList.remove('active');
      });
      const input = event.currentTarget || event.target;
      input.closest('[data-option]').classList.add('active');
      this.toggleSubmitButton(true);
    }

    /**
     * On submit form
     * @param {object} event
     */
    onSubmit(event) {
      event.preventDefault();

      // Get selected sheeting data
      this.formData = new FormData(this.form);
      this.id = this.formData.get('sheeting_id');
      this.input = this.querySelector(`input[value="${this.id}"]`);
      this.color = this.input.dataset?.sheetingColor;
      this.quality = this.input.dataset?.sheetingQuality;
      this.title = this.input.dataset?.sheetingTitle;
      this.sku = this.input.dataset?.sheetingSku;
      this.image = this.input.dataset.sheetingImage || '';

      // Update configurator inputs
      this.sheetingPlaceholder.querySelector('input[name="sheeting_id"]').value = this.id;
      this.sheetingPlaceholder.querySelector('input[name="sheeting_title"]').value = this.title;
      this.sheetingPlaceholder.querySelector('input[name="sheeting_color"]').value = this.color;
      this.sheetingPlaceholder.querySelector('input[name="sheeting_sku"]').value = this.sku;

      // Trigger change event on configurator inputs
      this.sheetingPlaceholder
        .querySelector('input[name="sheeting_id"]')
        .dispatchEvent(new Event('change'));

      // Update sheeting image
      if (this.image) {
        this.sheetingImage.innerHTML = `<img src="${this.image}" width="48" height="48" loading="lazy" alt="${this.title}" class="aspect-square w-14 overflow-hidden rounded-lg object-cover">`;
      } else {
        this.sheetingImage.innerHTML = '';
      }

      // Update sheeting title
      if (this.color) {
        this.sheetingTitle.innerHTML = `${this.title} - ${this.color}`;
      } else {
        this.sheetingTitle.innerHTML = this.title;
      }

      // Toggle sheeting placeholder/button
      this.toggleSheetingPlaceholder(true);

      // Close the sheeting picker drawer
      this.closeButton.dispatchEvent(new Event('click'));
    }

    /**
     * Toggle submit button
     * @param {boolean} active
     */
    toggleSubmitButton(active = true) {
      if (active) {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.innerHTML = this.translations.submit_button_text;
        this.submitButton.setAttribute('aria-label', this.translations.submit_button_text);
      } else {
        this.submitButton.setAttribute('disabled', true);
        this.submitButton.innerHTML = this.translations.submit_button_text_disabled;
        this.submitButton.setAttribute('aria-label', this.translations.submit_button_text_disabled);
      }
    }

    /**
     * Toggle sheeting placeholder/button
     * @param {boolean} active (true a sheeting is selected, false no sheeting is selected)
     */
    toggleSheetingPlaceholder(active = true) {
      if (!this.sheetingButton || !this.sheetingPlaceholder) {
        return;
      }

      if (active) {
        this.sheetingPlaceholder.classList.remove('pointer-events-none');
        this.sheetingPlaceholder.classList.remove('overflow-hidden');
        this.sheetingPlaceholder.classList.remove('opacity-0');
        this.sheetingPlaceholder.style.maxHeight = '5rem';
        this.sheetingButton.classList.add('pointer-events-none');
        this.sheetingButton.classList.add('overflow-hidden');
        this.sheetingButton.classList.add('opacity-0');
        this.sheetingButton.style.maxHeight = '0';
      } else {
        this.sheetingPlaceholder.classList.add('pointer-events-none');
        this.sheetingPlaceholder.classList.add('overflow-hidden');
        this.sheetingPlaceholder.classList.add('opacity-0');
        this.sheetingPlaceholder.style.maxHeight = '0';
        this.sheetingButton.classList.remove('pointer-events-none');
        this.sheetingButton.classList.remove('overflow-hidden');
        this.sheetingButton.classList.remove('opacity-0');
        this.sheetingButton.style.maxHeight = '5rem';
      }
    }

    /**
     * Filter sheetings based on sheeting type
     * @param {string} type - translucent / obscuring
     */
    filter(type) {
      if (!type) {
        return;
      }
      let firstInput = false;
      this.inputs.forEach((input) => {
        const inputType = input.dataset?.sheetingType;
        if (inputType == type) {
          input.closest('[data-option]').classList.remove('hidden');

          // Make the first option visible in the price calculator
          // only set the id so we know the step is not completed yet
          if (!firstInput) {
            firstInput = input;
            this.sheetingPlaceholder.querySelector('input[name="sheeting_id"]').value = input.value;
            this.sheetingPlaceholder.querySelector('input[name="sheeting_title"]').value = '';
            this.sheetingPlaceholder.querySelector('input[name="sheeting_color"]').value = '';
            this.sheetingPlaceholder.querySelector('input[name="sheeting_sku"]').value = '';
            this.sheetingPlaceholder
              .querySelector('input[name="sheeting_id"]')
              .dispatchEvent(new Event('change'));
          }
        } else {
          input.closest('[data-option]').classList.add('hidden');
        }
      });
    }

    /**
     * Clear sheeting picker
     */
    clear() {
      this.toggleSheetingPlaceholder(false);

      // Clear inputs in drawer
      this.inputs.forEach((input) => {
        input.closest('[data-option]').classList.remove('active');
        input.checked = false;
      });

      // Reset submit in drawer
      this.toggleSubmitButton(false);

      // Clear inputs in configurator
      this.sheetingImage.innerHTML = '';
      this.sheetingTitle.innerHTML = '';
    }
  }

  window.SheetingPicker = SheetingPicker;

  customElements.define('sheeting-picker', SheetingPicker);
}
